import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { DashboardLayout } from 'layouts/DashboardLayout';
import { MainPageLayout } from 'layouts/MainPageLayout';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { getIsReportAnIssueMode } from 'store/ticket-service/selector';
import { getIsOpenUserProfile } from 'store/user-service/selector';

import { Profile } from 'components/Profile';
import { ReportAnIssue } from 'components/ReportAnIssue';
import { GlowingCard } from 'components/ui/GlowingCard';

import { ReactComponent as CreateRequest } from 'assets/icons/create-request.svg';
import { ReactComponent as Document } from 'assets/icons/document.svg';
import { ReactComponent as DesignStep } from 'assets/icons/design-step.svg';
import { ReactComponent as TestCasesStep } from 'assets/icons/test-cases-step.svg';
import { ReactComponent as EditRequest } from 'assets/icons/edit-request.svg';
import { ReactComponent as BenchmarkStep } from 'assets/icons/benchmark-step.svg';
import { ReactComponent as HistoryChanges } from 'assets/icons/history-changes.svg';

import { CardsGrid, GlowingLine, DashboardContainer } from './NewDashboard.styles';
import { saveUserDataToCookies } from 'utils/cookieUtils';


export default function NewDashboard() {
	const navigate = useNavigate();
	const { t: dashboardTranslate } = useTranslation(NamespacesList['dashboard']);

	const isOpenUserProfile = useSelector(getIsOpenUserProfile);
	const reportAnIssueMode = useSelector(getIsReportAnIssueMode);

	const handleCreateBRD = () => {
		navigate(ROUTES.BRD);
	};
		/* 
			* Handle the click event to open the coder application
	* Save the user data and token in the cookies
	* Open the coder application
	* @param {Object} userData - The user data
	* @param {string} token - The token
	* @returns {void}
*/
	const handleOtherApps = () => {
		//navigate(ROUTES.CODER);
		const userData = JSON.parse(localStorage.getItem('user') || '{}');
		const token = localStorage.getItem('token') || '';
		
		// Sauvegarder les données utilisateur et le token dans les cookies
		// pour qu'elles soient accessibles dans l'autre application
		saveUserDataToCookies(userData, token);
		
		
		
		
		window.open(`${window.location.href}coder`, '_blank');

		console.log('Ellogy Coder coming soon');
	};

	const handleGenerateProductPlan = () => {
		console.log('Generate Product Plan - Coming soon');
	};

	const handleGenerateDesignDocs = () => {
		console.log('Generate Design Documents - Coming soon');
	};

	const handleGenerateWireframes = () => {
		console.log('Generate Wireframes - Coming soon');
	};

	const handleGenerateTestingDocs = () => {
		console.log('Generate Testing Documents - Coming soon');
	};

	const handleChangeManager = () => {
		console.log('Change Manager - Coming soon');
	};

	const handleCodeTester = () => {
		console.log('Code Tester - Coming soon');
	};

	const handleTechnicalWriter = () => {
		console.log('Technical Writer - Coming soon');
	};


	// Handle different view modes
	if (isOpenUserProfile) {
		return (
			<MainPageLayout>
				<Profile />
			</MainPageLayout>
		);
	}

	if (reportAnIssueMode) {
		return (
			<MainPageLayout>
				<ReportAnIssue />
			</MainPageLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardContainer
				sx={{
					height: '100%',
					background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
					padding: '40px',
					'@media (max-width: 1024px)': {
						padding: '24px',
					},
					'@media (max-width: 768px)': {
						padding: '20px',
					},
					borderRadius: '12px',
					display: 'flex',
					flexDirection: 'column',
					//gap: '40px',
					boxShadow: '0px 8px 32px rgba(71, 134, 255, 0.08)',
					overflow: 'auto',
					maxWidth: '100%',
					width: '100%',
				}}
			>
				{/* Welcome Section */}
				<Box
					sx={{
						textAlign: 'center',
						marginBottom: '40px',
						maxWidth: '800px',
						marginX: 'auto',
					}}
				>
					<Typography
						variant="h4"
						sx={{
							fontSize: '32px',
							fontWeight: 700,
							marginBottom: '6px',
							color: '#396EDA',
							'@media (max-width: 768px)': {
								fontSize: '24px',
							},
						}}
					>
						{dashboardTranslate('welcome.title')}
					</Typography>
					<Typography
						sx={{
							fontSize: '18px',
							color: '#396EDA',
							maxWidth: '600px',
							margin: '0 auto 32px auto',
							'@media (max-width: 768px)': {
								fontSize: '16px',
								marginBottom: '4px',
							},
						}}
					>
						{dashboardTranslate('welcome.subtitle')}
					</Typography>
					<GlowingLine />
				</Box>

				{/* Dashboard Cards Grid */}
				<CardsGrid component="ul">
					<GlowingCard
						icon={<BenchmarkStep style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.productManager.title')}
						description={dashboardTranslate('roles.productManager.description')}
						onClick={handleGenerateProductPlan}
						disabled={true}
					/>
					<GlowingCard
						icon={<CreateRequest style={{ width: '24px', height: '24px', color: '#4786FF' }} />}
						title={dashboardTranslate('roles.businessAnalyst.title')}
						description={dashboardTranslate('roles.businessAnalyst.description')}
						onClick={handleCreateBRD}
						disabled={false}
					/>
					
					<GlowingCard
						icon={<DesignStep style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.softwareDesigner.title')}
						description={dashboardTranslate('roles.softwareDesigner.description')}
						onClick={handleGenerateDesignDocs}
						disabled={true}
					/>

					<GlowingCard
						icon={<EditRequest style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.uiUxDesigner.title')}
						description={dashboardTranslate('roles.uiUxDesigner.description')}
						onClick={handleGenerateWireframes}
						disabled={true}
					/>

					<GlowingCard
						icon={<Document style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.softwareDeveloper.title')}
						description={dashboardTranslate('roles.softwareDeveloper.description')}
						onClick={handleOtherApps}
						disabled={false}
						//disabled={true}
					/>

					<GlowingCard
						icon={<TestCasesStep style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.qaManager.title')}
						description={dashboardTranslate('roles.qaManager.description')}
						onClick={handleGenerateTestingDocs}
						disabled={true}
					/>
					<GlowingCard
						icon={<HistoryChanges style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.changeManager.title')}
						description={dashboardTranslate('roles.changeManager.description')}
						onClick={handleChangeManager}
						disabled={true}
					/>

					<GlowingCard
						icon={<TestCasesStep style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.codeTester.title')}
						description={dashboardTranslate('roles.codeTester.description')}
						onClick={handleCodeTester}
						disabled={true}
					/>

					<GlowingCard
						icon={<Document style={{ width: '24px', height: '24px', color: '#9CA3AF' }} />}
						title={dashboardTranslate('roles.technicalWriter.title')}
						description={dashboardTranslate('roles.technicalWriter.description')}
						onClick={handleTechnicalWriter}
						disabled={true}
					/>
				</CardsGrid>
			</DashboardContainer>
		</DashboardLayout>
	);
}